import React, { useCallback, useMemo, useState } from 'react';
import { useLocale } from '@honeybook/i18n-react';
import HBCombobox from '../HBCombobox/HBCombobox';
import { ComboboxItem, ComboboxSection } from '../HBCombobox/HBCombobox.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import {
    formatLongUtcOffset,
    formatDateByTimezone,
    SupportedLocale
} from '@honeybook/i18n';
import HBText from '../HBText/HBText';
import { useHBUI } from '../HBUIProvider/HBUIProvider';
import { TimezoneInfo, HBTimezoneInputProps } from './HBTimezoneInput.types';

/** Display order of region sections in the dropdown. */
const TIMEZONE_REGION_ORDER = [
    'Americas',
    'Europe & Africa',
    'Asia & Pacific',
    'Other'
] as const;

/** One of the region section labels rendered in the dropdown. */
type TimezoneRegionLabel = (typeof TIMEZONE_REGION_ORDER)[number];

/**
 * Maps IANA region prefixes to the section labels shown in HBTimezoneInput.
 * Values are constrained to the labels in TIMEZONE_REGION_ORDER so a typo'd
 * label fails type-checking instead of silently dropping that region's
 * timezones in buildRegionSections.
 */
const TIMEZONE_REGION_GROUPS: Record<string, TimezoneRegionLabel> = {
    America: 'Americas',
    Pacific: 'Asia & Pacific',
    Europe: 'Europe & Africa',
    Africa: 'Europe & Africa',
    Atlantic: 'Europe & Africa',
    Asia: 'Asia & Pacific',
    Australia: 'Asia & Pacific',
    UTC: 'Other'
};

function timezoneToComboboxItem(
    tz: TimezoneInfo,
    now: Date,
    locale: SupportedLocale
): ComboboxItem<TimezoneInfo> {
    const longOffset = formatLongUtcOffset(tz.id, now);

    return {
        value: tz,
        label: tz.label,
        description: formatDateByTimezone(now, locale, tz.id, 'time'),
        rightContent: (
            <HBText type="body12" color="text.tertiary">
                {longOffset}
            </HBText>
        ),
        searchTerms: [tz.id, longOffset, tz.region],
        testIdName: tz.id
    };
}

function buildRegionSections(
    timezones: TimezoneInfo[],
    now: Date,
    locale: SupportedLocale
): ComboboxSection<TimezoneInfo>[] {
    const byGroup = new Map<string, ComboboxItem<TimezoneInfo>[]>();

    for (const tz of timezones) {
        const group = TIMEZONE_REGION_GROUPS[tz.region] ?? 'Other';
        if (!byGroup.has(group)) {
            byGroup.set(group, []);
        }
        byGroup.get(group)!.push(timezoneToComboboxItem(tz, now, locale));
    }

    return TIMEZONE_REGION_ORDER.filter(g => byGroup.has(g)).map(g => ({
        id: g,
        title: g,
        items: byGroup.get(g)!
    }));
}

const HBTimezoneInput = ({
    id,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    error = false,
    value,
    onChange,
    timezones,
    suggestedTimezoneIds,
    suggestedSectionLabel = 'Suggested',
    placeholder = 'Select timezone',
    searchPlaceholder = 'Search',
    size = 'sm',
    disabled = false,
    width,
    testId,
    testIdName
}: HBTimezoneInputProps) => {
    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'timezone-input',
        componentName: 'HBTimezoneInput'
    });

    const { locale } = useLocale();
    const { isHintedEllipsis } = useHBUI();

    // Each item shows the current local time in its zone. The snapshot is taken
    // once here and refreshed every time the menu opens (see handleOpenChange),
    // so the times are current when the user actually reads them rather than
    // frozen at initial mount.
    const [now, setNow] = useState(() => new Date());

    const handleOpenChange = useCallback((open: boolean) => {
        if (open) {
            setNow(new Date());
        }
    }, []);

    const sections = useMemo<ComboboxSection<TimezoneInfo>[]>(() => {
        const result: ComboboxSection<TimezoneInfo>[] = [];

        if (suggestedTimezoneIds?.length) {
            const items = suggestedTimezoneIds
                .map(id => timezones.find(tz => tz.id === id))
                .filter((tz): tz is TimezoneInfo => tz !== undefined)
                .map(tz => timezoneToComboboxItem(tz, now, locale));

            if (items.length) {
                result.push({
                    id: 'suggested',
                    title: suggestedSectionLabel,
                    items
                });
            }
        }

        result.push(...buildRegionSections(timezones, now, locale));
        return result;
    }, [timezones, suggestedTimezoneIds, suggestedSectionLabel, locale, now]);

    const selectedTz = useMemo(
        () => timezones.find(tz => tz.id === value) ?? null,
        [timezones, value]
    );

    return (
        <HBCombobox<TimezoneInfo>
            value={selectedTz}
            onChange={tz => onChange(tz.id)}
            onOpenChange={handleOpenChange}
            sections={sections}
            id={id}
            aria-label={ariaLabelledBy ? undefined : ariaLabel ?? 'Timezone'}
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
            aria-invalid={ariaInvalid}
            error={error}
            placeholder={placeholder}
            size={size}
            disabled={disabled}
            width={width ?? '100%'}
            showOverflowTooltip={isHintedEllipsis}
            searchPlaceholder={searchPlaceholder}
            menuWidth="320px"
            menuMaxWidth="320px"
            menuMaxHeight="320px"
            testId={finalTestId}
            testIdName={testIdName}
        />
    );
};

export default HBTimezoneInput;
